import React from 'react';
import { Bed, Stethoscope, User } from 'lucide-react';
import type { SaleType } from '../../types/pharmacy-pos';

interface TypeSwitchProps {
  selectedType: SaleType;
  onTypeChange: (type: SaleType) => void;
  disabled?: boolean;
}

export const TypeSwitch: React.FC<TypeSwitchProps> = ({
  selectedType,
  onTypeChange,
  disabled = false
}) => {
  const types: Array<{ value: SaleType; label: string; icon: React.ReactNode }> = [
    { value: 'ipd', label: 'IPD Patient', icon: <Bed className="w-4 h-4" /> },
    { value: 'opd', label: 'OPD Patient', icon: <Stethoscope className="w-4 h-4" /> },
    { value: 'walkin', label: 'Phar Customer', icon: <User className="w-4 h-4" /> }
  ];

  return (
    <div className="flex space-x-2">
      {types.map((type) => (
        <button
          key={type.value}
          onClick={() => onTypeChange(type.value)}
          disabled={disabled}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all
            ${selectedType === type.value
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          {type.icon}
          <span>{type.label}</span>
        </button>
      ))}
    </div>
  );
};

interface IPDFieldsProps {
  admissionId?: string;
  bedId?: string;
  encounterId?: string;
  onAdmissionChange: (admissionId: string) => void;
  onBedChange: (bedId: string) => void;
  onEncounterChange: (encounterId: string) => void;
  disabled?: boolean;
}

export const IPDFields: React.FC<IPDFieldsProps> = ({
  admissionId,
  bedId,
  encounterId,
  onAdmissionChange,
  onBedChange,
  onEncounterChange,
  disabled = false
}) => {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Admission No <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={admissionId || ''}
          onChange={(e) => onAdmissionChange(e.target.value)}
          placeholder="Enter admission no"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Bed No <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={bedId || ''}
          onChange={(e) => onBedChange(e.target.value)}
          placeholder="Enter bed no"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Encounter ID
        </label>
        <input
          type="text"
          value={encounterId || ''}
          onChange={(e) => onEncounterChange(e.target.value)}
          placeholder="Optional"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>
    </div>
  );
};

interface OPDFieldsProps {
  doctorId?: string;
  visitNo?: string;
  prescriptionNo?: string;
  onDoctorChange: (doctorId: string) => void;
  onVisitNoChange: (visitNo: string) => void;
  onPrescriptionNoChange: (prescriptionNo: string) => void;
  disabled?: boolean;
}

export const OPDFields: React.FC<OPDFieldsProps> = ({
  doctorId,
  visitNo,
  prescriptionNo,
  onDoctorChange,
  onVisitNoChange,
  onPrescriptionNoChange,
  disabled = false
}) => {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Doctor
        </label>
        <input
          type="text"
          value={doctorId || ''}
          onChange={(e) => onDoctorChange(e.target.value)}
          placeholder="Optional"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Visit No
        </label>
        <input
          type="text"
          value={visitNo || ''}
          onChange={(e) => onVisitNoChange(e.target.value)}
          placeholder="Optional"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Prescription No
        </label>
        <input
          type="text"
          value={prescriptionNo || ''}
          onChange={(e) => onPrescriptionNoChange(e.target.value)}
          placeholder="Optional"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>
    </div>
  );
};

interface WalkInFieldsProps {
  name?: string;
  phone?: string;
  address?: string;
  onNameChange: (name: string) => void;
  onPhoneChange: (phone: string) => void;
  onAddressChange: (address: string) => void;
  disabled?: boolean;
}

export const WalkInFields: React.FC<WalkInFieldsProps> = ({
  name,
  phone,
  address,
  onNameChange,
  onPhoneChange,
  onAddressChange,
  disabled = false
}) => {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Customer Name
        </label>
        <input
          type="text"
          value={name || ''}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Optional"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Mobile No
        </label>
        <input
          type="tel"
          value={phone || ''}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="Optional"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>

      <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address
        </label>
        <input
          type="text"
          value={address || ''}
          onChange={(e) => onAddressChange(e.target.value)}
          placeholder="Optional"
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
        />
      </div>
    </div>
  );
};
